// 대장간 강화 순간 연출의 순수 모델(c017, DOM 없음). Ev.GearEnhance(a 결과, b 새 단계)를 연출 박자로 바꾸고,
// 강화 카드 위 작은 캔버스에 그릴 불꽃·불씨·균열·충격 고리를 고정 크기 풀로 진행한다(생성 뒤 할당 없음). 그리기는 ui/forgeFx.ts.
// 좌표: 카드 왼쪽 위 기준 px. 타격점 = 카드 가운데, 장비 이름 줄(+N 이름) 높이 — 망치가 장비를 친다. y는 아래로 +.
// 박자: 성공 = 망치 타격 섬광(카드 안, 짧게) + 위로 솟는 불꽃 + 떠오르는 불씨 + 금테 빛.
//       큰 성공(+10·+13·+15) = 더 센 섬광·불꽃 두 배·충격 고리 둘·카드를 가로지르는 빛 띠, 단계마다 색(금 / 진홍금 / 백금).
//       유지(실패) = 섬광 없음, 짧은 회청 균열 + 아래로 떨어지는 어두운 불씨. 하락 = 더 긴 두 갈래 붉은 균열 + 불씨 많이.
//       보호 = 균열이 시작되다 청색 방패 호에 막힌다 + 청색 알갱이.
// 초월(+16~+20, C4): 기존 강화 연출 위에 단계(step 1~5)마다 색이 다른 화려한 박자. 성공 = 더 센 섬광·불꽃·충격 고리 2~4개·빛 띠, +17부터 빛기둥,
//       +19부터 회전 광선, +20은 무지개빛(청·자·금 순환) 불꽃. 실패 = 그 단계 색으로 달궈진 균열(단계는 안 내려간다). 초월 카드(transcend-card-<부위>) 위에 그린다.
// 움직임 줄이기(prefers-reduced-motion): 입자·고리·빛 띠 없이 금테(또는 회청·붉은 테) 빛만 옅게 켜졌다 꺼지고 균열은 그 자리에 정지한 채 흐려진다.

import { FlashLimiter } from '../vfx/flash';

/** Ev.GearEnhance a */
export const EnhanceOutcome = { Success: 0, Keep: 1, Down: 2, Protected: 3 } as const;
/** 큰 성공 문턱(새 단계) */
export const BIG_ENHANCE = [10, 13, 15] as const;

export type ForgeBeatKind = 'success' | 'big' | 'keep' | 'down' | 'protected' | 'transcend' | 'transcendFail';
export interface ForgeBeat {
  readonly kind: ForgeBeatKind;
  /** 큰 성공 단계(0 = 아님, 1 = +10, 2 = +13, 3 = +15) */
  readonly tier: number;
  /** 초월 단계(0 = 일반 강화, 1..5 = +16..+20; 실패는 시도한 단계) */
  readonly step: number;
  /** 연출 길이(초) */
  readonly dur: number;
  /** 망치 섬광 세기(0 = 없음). 카드 안 국소 섬광이며 FlashLimiter를 거친다 */
  readonly flash: number;
  /** 솟는 불꽃 수 / 떠오르는 불씨 수 / 떨어지는 불씨 수 */
  readonly sparks: number;
  readonly motes: number;
  readonly embers: number;
  /** 균열 마디 수(0 = 없음)와 갈래 수 */
  readonly crack: number;
  readonly branches: number;
  /** 충격 고리 수 */
  readonly rings: number;
  /** 카드 테 빛 색(0xRRGGBB)·세기 */
  readonly rim: number;
  readonly rimAlpha: number;
  /** 주색(불꽃·고리) */
  readonly color: number;
  /** 빛 띠(큰 성공) */
  readonly sheen: boolean;
  /** 방패 호(보호) */
  readonly shield: boolean;
}

const GOLD = 0xffc94a, CRIMSON_GOLD = 0xff7a3a, PLATINUM = 0xfff2c8, ASH = 0x8ea2c4, BLOOD_EMBER = 0xff4a2a, WARD = 0x6ac8ff;

/** 초월 단계(1..5)별 색: 청백 · 보라 · 분홍 · 황금 · 백금(+20은 무지개 순환) */
export const TRANSCEND_COLOR: readonly number[] = [0x6fd6ff, 0xa78bff, 0xff6fd8, 0xffd36a, 0xfff6e0];
/** +20 무지개 불꽃이 순환하는 색 */
export const PRISM_COLORS: readonly number[] = [0x6fd6ff, 0xff6fd8, 0xffd36a];

/** Ev.GearEnhance가 초월인가: 시작 단계(c)가 +15 이상(일반 강화는 +15에서 시작하지 못한다) */
export function isTranscendEvent(from: number, to: number): boolean { return from >= 15 || to > 15; }

/** 초월 박자. step = 도착(성공) 또는 시도한(실패) 단계 1..5 */
export function transcendBeat(success: boolean, step: number): ForgeBeat {
  const st = Math.max(1, Math.min(5, Math.round(step)));
  const color = TRANSCEND_COLOR[st - 1];
  if (success) {
    return {
      kind: 'transcend', tier: 0, step: st, dur: 1.5 + 0.12 * st, flash: 0.8 + 0.03 * st, sparks: 70 + 10 * st, motes: 12, embers: 0, crack: 0, branches: 0,
      rings: 2 + (st >= 3 ? 1 : 0) + (st >= 5 ? 1 : 0), rim: color, rimAlpha: 1, color, sheen: true, shield: false,
    };
  }
  return { kind: 'transcendFail', tier: 0, step: st, dur: 1.15, flash: 0, sparks: 0, motes: 6, embers: 18, crack: 6, branches: 2, rings: 0, rim: color, rimAlpha: 0.6, color, sheen: false, shield: false };
}

/** 결과·새 단계 → 연출 박자. from = 시작 단계(Ev.GearEnhance c): +15 이상이면 초월 */
export function forgeBeat(outcome: number, to: number, from = 0): ForgeBeat {
  if (isTranscendEvent(from, to)) {
    if (outcome === EnhanceOutcome.Success) return transcendBeat(true, to - 15);
    return transcendBeat(false, from - 15 + 1);
  }
  if (outcome === EnhanceOutcome.Success) {
    // 문턱 단계에 도달한 그 강화만(+10·+13·+15). 그 사이 단계는 보통 성공
    const tier = BIG_ENHANCE.indexOf(to as (typeof BIG_ENHANCE)[number]) + 1;
    if (tier > 0) {
      const color = tier === 3 ? PLATINUM : tier === 2 ? CRIMSON_GOLD : GOLD;
      return { kind: 'big', tier, step: 0, dur: 1.3, flash: 0.75 + 0.08 * tier, sparks: 48 + 10 * tier, motes: 14, embers: 0, crack: 0, branches: 0, rings: 2, rim: color, rimAlpha: 1, color, sheen: true, shield: false };
    }
    return { kind: 'success', tier: 0, step: 0, dur: 0.95, flash: 0.6, sparks: 34, motes: 8, embers: 0, crack: 0, branches: 0, rings: 1, rim: GOLD, rimAlpha: 0.75, color: GOLD, sheen: false, shield: false };
  }
  if (outcome === EnhanceOutcome.Down) return { kind: 'down', tier: 0, step: 0, dur: 1.1, flash: 0, sparks: 0, motes: 0, embers: 22, crack: 7, branches: 2, rings: 0, rim: BLOOD_EMBER, rimAlpha: 0.55, color: BLOOD_EMBER, sheen: false, shield: false };
  if (outcome === EnhanceOutcome.Protected) return { kind: 'protected', tier: 0, step: 0, dur: 1.0, flash: 0, sparks: 0, motes: 8, embers: 6, crack: 3, branches: 1, rings: 0, rim: WARD, rimAlpha: 0.65, color: WARD, sheen: false, shield: true };
  return { kind: 'keep', tier: 0, step: 0, dur: 0.9, flash: 0, sparks: 0, motes: 0, embers: 14, crack: 5, branches: 1, rings: 0, rim: ASH, rimAlpha: 0.45, color: ASH, sheen: false, shield: false };
}

/** 입자 종류 */
export const PartKind = { Spark: 0, Mote: 1, Ember: 2 } as const;
/** 입자 풀 크기(큰 성공 최대 64 + 불씨 14 + 여유) */
export const FORGE_PARTICLES = 144;
/** 균열 점 풀(갈래 2 × 마디 7 + 시작점) */
export const FORGE_CRACK_PTS = 24;

/**
 * 연출 진행기. start로 박자를 걸고 step(dt)으로 진행한다. 입자 배열은 생성 때 한 번만 만든다.
 * 렌더러는 live 입자(0..n)와 균열 점(crackN), 시각 t, 박자 beat를 읽어 그린다.
 */
export class ForgeSim {
  beat: ForgeBeat | null = null;
  /** 연출 시작 뒤 지난 시간(초) */
  t = 0;
  /** 타격점(카드 기준 px)과 카드 크기 */
  ox = 0; oy = 0; w = 1; h = 1;
  /** 움직임 줄이기: 입자·고리·빛 띠 없음 */
  reduced = false;
  /** 망치 섬광을 실제로 건 세기(리미터가 막으면 0) */
  flashOn = 0;
  /** 카드 테 빛 배율(섬광 약화·1초 3회 규칙, ForgeGate가 정한다) */
  rimScale = 1;
  readonly px = new Float32Array(FORGE_PARTICLES);
  readonly py = new Float32Array(FORGE_PARTICLES);
  readonly vx = new Float32Array(FORGE_PARTICLES);
  readonly vy = new Float32Array(FORGE_PARTICLES);
  readonly age = new Float32Array(FORGE_PARTICLES);
  readonly life = new Float32Array(FORGE_PARTICLES);
  readonly kind = new Uint8Array(FORGE_PARTICLES);
  /** 입자 크기(px) */
  readonly size = new Float32Array(FORGE_PARTICLES);
  n = 0;
  readonly cx = new Float32Array(FORGE_CRACK_PTS);
  readonly cy = new Float32Array(FORGE_CRACK_PTS);
  /** 균열 점이 속한 갈래(같은 갈래끼리 잇는다) */
  readonly cb = new Uint8Array(FORGE_CRACK_PTS);
  crackN = 0;
  private seed = 1;

  private rnd(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  get active(): boolean { return this.beat !== null && this.t < this.beat.dur; }

  /** 박자를 건다. w,h = 카드 크기(px). seed가 같으면 같은 모양(결정적 캡처) */
  start(beat: ForgeBeat, w: number, h: number, seed: number, reduced: boolean, flashOn: number, rimScale = 1): void {
    this.rimScale = rimScale;
    this.beat = beat; this.t = 0; this.n = 0; this.crackN = 0;
    this.w = w; this.h = h; this.ox = w * 0.5; this.oy = Math.min(h * 0.12, 26);
    this.seed = (seed >>> 0) || 1;
    this.reduced = reduced;
    this.flashOn = flashOn;
    if (beat.crack > 0) this.buildCrack(beat);
    if (reduced) return;
    for (let i = 0; i < beat.sparks; i++) {
      // 위로 솟는 부채(-120°..-60°), 큰 성공은 더 넓고 빠르다
      const bigLike = beat.kind === 'big' || beat.kind === 'transcend';
      const spread = beat.kind === 'transcend' ? 1.6 : bigLike ? 1.35 : 1.0;
      const a = -Math.PI / 2 + (this.rnd() - 0.5) * spread;
      const sp = (bigLike ? 260 : 200) + this.rnd() * (bigLike ? 300 : 220);
      this.add(PartKind.Spark, this.ox + (this.rnd() - 0.5) * 16, this.oy, Math.cos(a) * sp, Math.sin(a) * sp, 0.45 + this.rnd() * 0.45, 1.8 + this.rnd() * 1.6);
    }
    for (let i = 0; i < beat.motes; i++) {
      // 떠오르는 불씨(보호는 청색 알갱이): 카드 폭에 흩어져 천천히 오른다
      const x = this.ox + (this.rnd() - 0.5) * this.w * 0.7;
      this.add(PartKind.Mote, x, this.oy + 10 + this.rnd() * 30, (this.rnd() - 0.5) * 30, -40 - this.rnd() * 50, 0.8 + this.rnd() * 0.5, 1.6 + this.rnd() * 1.6);
    }
    for (let i = 0; i < beat.embers; i++) {
      // 떨어지는 어두운 불씨: 균열 근처에서 아래로
      const x = this.ox + (this.rnd() - 0.5) * 60;
      this.add(PartKind.Ember, x, this.oy + this.rnd() * 20, (this.rnd() - 0.5) * 80, 10 + this.rnd() * 40, 0.6 + this.rnd() * 0.5, 1.8 + this.rnd() * 2.2);
    }
  }

  private add(kind: number, x: number, y: number, vx: number, vy: number, life: number, size: number): void {
    if (this.n >= FORGE_PARTICLES) return;
    const i = this.n++;
    this.kind[i] = kind; this.px[i] = x; this.py[i] = y; this.vx[i] = vx; this.vy[i] = vy; this.age[i] = 0; this.life[i] = life; this.size[i] = size;
  }

  /** 지그재그 균열: 타격점에서 아래 양옆으로 갈래마다 crack 마디 */
  private buildCrack(beat: ForgeBeat): void {
    const seg = Math.min(beat.crack, Math.floor((FORGE_CRACK_PTS - beat.branches) / Math.max(1, beat.branches)));
    const len = beat.kind === 'down' || beat.kind === 'transcendFail' ? Math.min(this.h * 0.6, 150) : beat.kind === 'protected' ? 34 : Math.min(this.h * 0.45, 100);
    for (let b = 0; b < beat.branches; b++) {
      const side = beat.branches === 1 ? (this.rnd() < 0.5 ? -1 : 1) : b === 0 ? -1 : 1;
      let x = this.ox, y = this.oy;
      this.push(x, y, b);
      const dirX = side * (0.35 + this.rnd() * 0.3);
      for (let k = 0; k < seg; k++) {
        const stepL = len / seg;
        x += dirX * stepL + (this.rnd() - 0.5) * stepL * 0.9;
        y += stepL * (0.75 + this.rnd() * 0.4);
        this.push(x, y, b);
      }
    }
  }

  private push(x: number, y: number, b: number): void {
    if (this.crackN >= FORGE_CRACK_PTS) return;
    this.cx[this.crackN] = x; this.cy[this.crackN] = y; this.cb[this.crackN] = b; this.crackN++;
  }

  /** dt초 진행(할당 없음). 끝난 입자는 마지막 것과 자리를 바꿔 지운다 */
  step(dt: number): void {
    if (this.beat === null) return;
    this.t += dt;
    let i = 0;
    while (i < this.n) {
      this.age[i] += dt;
      if (this.age[i] >= this.life[i]) {
        const j = --this.n;
        if (i !== j) {
          this.kind[i] = this.kind[j]; this.px[i] = this.px[j]; this.py[i] = this.py[j]; this.vx[i] = this.vx[j]; this.vy[i] = this.vy[j];
          this.age[i] = this.age[j]; this.life[i] = this.life[j]; this.size[i] = this.size[j];
        }
        continue;
      }
      const k = this.kind[i];
      // 불꽃: 중력 + 공기 저항, 불씨(위): 느린 흔들림, 불씨(아래): 중력
      if (k === PartKind.Spark) { this.vy[i] += 620 * dt; const d = Math.exp(-1.6 * dt); this.vx[i] *= d; this.vy[i] *= d; }
      else if (k === PartKind.Mote) { this.vx[i] += Math.sin((this.age[i] + i) * 7) * 40 * dt; }
      else { this.vy[i] += 260 * dt; }
      this.px[i] += this.vx[i] * dt; this.py[i] += this.vy[i] * dt;
      i++;
    }
    if (this.t >= this.beat.dur) { this.n = 0; }
  }

  /** 망치 섬광 세기(0..1): 0.025초에 올라 0.095초에 꺼진다(FLASH_SAFETY 1회 100ms 이하) */
  flashAt(): number {
    if (this.flashOn <= 0 || this.reduced) return 0;
    const t = this.t;
    if (t < 0.025) return this.flashOn * (t / 0.025);
    if (t < 0.095) return this.flashOn * (1 - (t - 0.025) / 0.07);
    return 0;
  }

  /** 카드 테 빛 세기(0..1) */
  rimAt(): number {
    const b = this.beat;
    if (b === null) return 0;
    const u = this.t / b.dur;
    if (u >= 1) return 0;
    const atk = 0.08;
    return b.rimAlpha * this.rimScale * (u < atk ? u / atk : Math.pow(1 - (u - atk) / (1 - atk), 1.4)) * (this.reduced ? 0.7 : 1);
  }

  /** 균열이 그려진 정도(0..1): 0.12초에 다 그어지고 마지막 35%에서 흐려진다(보호는 0.06초에 막힌다) */
  crackAt(): { reach: number; alpha: number } {
    const b = this.beat;
    CRACK.reach = 0; CRACK.alpha = 0;
    if (b === null || this.crackN === 0) return CRACK;
    const u = this.t / b.dur;
    CRACK.reach = this.reduced ? 1 : Math.min(1, this.t / (b.shield ? 0.07 : 0.12));
    CRACK.alpha = u >= 1 ? 0 : u < 0.65 ? 1 : 1 - (u - 0.65) / 0.35;
    return CRACK;
  }
}
const CRACK = { reach: 0, alpha: 0 };

/** 섬광 약화 배율(망치 섬광·테 빛) */
export const FORGE_REDUCE_FLASH = 0.4;
/** 1초 3회·간격 334ms 예산을 넘은 강화의 테 빛 배율(옅게만) */
export const FORGE_RIM_OVER = 0.25;

/**
 * 강화 연출의 밝은 요소 문지기(PR #161 리뷰 P2): 망치 섬광과 카드 테 빛이 각각 FLASH_SAFETY(1초 3회·간격 334ms) 리미터를 거친다.
 * 예산을 넘은 섬광은 0, 테 빛은 FORGE_RIM_OVER로 옅게. 섬광 약화(게임 설정)면 둘 다 40%, 움직임 줄이기면 섬광 없음
 */
export class ForgeGate {
  private readonly flash = new FlashLimiter();
  private readonly rim = new FlashLimiter();
  /** 마지막 판정: 섬광 세기·테 빛 배율 */
  flashOn = 0;
  rimScale = 1;
  readonly stats = { flashes: 0, flashBlocked: 0, rims: 0, rimDimmed: 0 };

  begin(nowMs: number, beat: ForgeBeat, reduceFlash: boolean, reduced: boolean): void {
    const k = reduceFlash ? FORGE_REDUCE_FLASH : 1;
    let f = beat.flash * k;
    if (f > 0 && !reduced) {
      if (this.flash.request(nowMs, 100, f)) this.stats.flashes++;
      else { f = 0; this.stats.flashBlocked++; }
    } else f = 0;
    this.flashOn = f;
    if (this.rim.admit(nowMs)) { this.rimScale = k; this.stats.rims++; }
    else { this.rimScale = k * FORGE_RIM_OVER; this.stats.rimDimmed++; }
  }
}
